import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { object, string, array } from 'yup';
import { useFormik } from 'formik';

// Types
import { ButtonType } from '../../types/button-type';
import { ElementSize } from '../../types/element-size';

import ConfigurationCycle, { ConfigurationCycleOption } from '../../components/ConfigurationCycle/ConfigurationCycle';
import ControllerTabs from '../../components/ControllerTabs/ControllerTabs';
import AddControllerModal from '../../components/AddControllerModal/AddControllerModal';
import InputControl from '../../components/InputControl/InputControl';
import InteractiveButton from '../../components/InteractiveButton/InteractiveButton';

import { Dispatch, RootState } from '../../store/store';
import { connect } from 'react-redux';
import SelectOption, { Option, SelectedValue } from '../../components/SelectOption/SelectOption';
import { DATA_RETRIEVAL_INTERVAL, TEXT_LENGTH_LIMITS } from '../../constants/common';
import { CycleFormValues, ItemList } from '../../types/cycle';
import { CHANNELS, THERMOCOUPLES } from '../../constants/cycle';
import { createPath, generatePath, useNavigate, useParams } from 'react-router-dom';
import ROUTE from '../../constants/route-constants';
import { Location } from '../../store/models/locations';
import { Contract } from '../../store/models/contracts';
import { Screen } from '../../store/models/screens';
import { Controller } from '../../store/models/controllers';
import SpinnerLoader from '../../components/SpinnerLoader/SpinnerLoader';
import ComponentNameCycleList from '../../enums/component-name-cycle-list.enum';
import CycleStatus from '../../enums/cycle-status.enum';
import { ICycleStatusListItem } from './types';
import StatusListItem from './StatusListItem';
import InputDateTimeControl from '../../components/InputDateTimeControl/InputDateTimeControl';
import { IChannelName } from '../../types/channel-types';
import { capitalizeFirstLetter, getChannelNames } from '../AddCycle/utils';
import CycleConflictBanner from '../../components/CycleConflictBanner/CycleConflictBanner';
import { CycleConflictError, getCycleConflictError } from '../../utils/cycle-conflict-error';
import CycleSettingsInput, { CycleSettingsFormValues } from '../../components/CycleSettingsInput/CycleSettingsInput';
import CycleSettingsEnum from '../../enums/cycle-setting.enum';
import CycleMeasurement from '../../enums/cycle-measurement';
import { createCycleSettingsValidationSchema, convertCycleSettingsUnits, applyDefaultTolerances } from '../../utils/cycleSettingsForm';

type ComponentProps = ReturnType<typeof mapState> &
  ReturnType<typeof mapDispatch>;

const KEY_TO_CONVERT_LIST = 'cycle_controllers';

const ROW_BLOCK_CLASS = 'grid grid-cols-[130px_1fr] items-center gap-x-3';
const ROW_LABEL_CLASS = 'text-sm 2xl:text-base font-medium text-black dark:text-white';

const STATUS_LIST: ICycleStatusListItem[] = [
  {
    text: 'Deactivate Cycle',
    value: CycleStatus.InActive,
  },
  {
    text: 'Activate Cycle',
    value: CycleStatus.Active,
  },
  {
    text: 'Mark Complete',
    value: CycleStatus.Completed,
  },
]

const thermocoupleOptions: { name: string, value: string }[] = Object.keys(THERMOCOUPLES).map((item) => ({ name: item, value: item }));

const EditCycle: React.FC<ComponentProps> = ({
  user,
  controllers,
  locations,
  contracts,
  getControllersByOrganizationId,
  screens,
  updateCycle,
  getLocations,
  getContractsByLocationId,
  getContractById,
  getScreensByContractId,
  getCycleById,
  cycle,
  getChannels,
  clearChannels,
  getPlannedRecipe,
  getRecipe,
  getRecipeSteps,
}) => {
  const navigate = useNavigate();

  const { clientId, contractId, screenId, id } = useParams();

  const [editedController, setEditedController] = useState<Controller | null>(null);
  const [isAddControllerModalOpen, setIsAddControllerModalOpen] = useState<boolean>(false);
  const [loadingChannels, setLoadingChannels] = useState<boolean>(false);
  const [channelsByController, setChannelsByController] = useState<Record<string, IChannelName[]>>({});
  const [cycleConflict, setCycleConflict] = useState<CycleConflictError | null>(null);
  const [cycleSettingsMeasurement, setCycleSettingsMeasurement] = useState<CycleMeasurement>(CycleMeasurement.Celsius);
  const [plannedRecipeRaw, setPlannedRecipeRaw] = useState<{ source: 'recipe' | 'pending' | null; settings: any } | null>(null);

  const getPath = (): string => {
    if (screenId) {
      return generatePath(ROUTE.SCREEN_CYCLES, { clientId, contractId, screenId });
    }

    let path = generatePath(ROUTE.LOCATION_CONTRACT_SCREENS, { clientId, contractId })
    const searchParams = new URLSearchParams({ status: cycle.status }).toString();
    path = createPath({
      pathname: path,
      search: `?${searchParams}`,
    });

    return path;
  }

  const cycleValidationSchema = useMemo(
    () =>
      object({
        name: string()
        .required('Required')
        .min(TEXT_LENGTH_LIMITS.MIN, `Min first name length is ${TEXT_LENGTH_LIMITS.MIN} characters`)
        .max(TEXT_LENGTH_LIMITS.MAX, `Max first name length is ${TEXT_LENGTH_LIMITS.MAX} characters`)
        .matches(
          /^[a-zA-Z0-9 _-]+$/,
          'Name can only contain letters, numbers, spaces, underscores, and hyphens'
        )
        .test(
          'no-trim-spaces',
          'Name cannot start or end with spaces',
          value => value ? value.trim() === value : true
        ),
      list: array()
        .min(1, 'Must contain at least one controller')
        .test('No channel or thermocouple selected', 'Each controller must have at least one channel or thermocouple', (value) => {
          return value?.every((item) => item.thermocouples.length > 0 || item.channels.length > 0);
        }),
      locationId: string()
        .required('Required'),
      contractId: string()
        .required('Required'),
      screenId: string().nullable().optional(),
      start_at: string()
        .nullable()
        .transform((value) => (value === '' ? null : value))
        .typeError('Must be a valid timestamp')
        .test(
          'date_must_be_now_or_earlier',
          'Start date must be the current time or earlier',
          function (value) {
            if (!value) return true;

            const date = new Date(Number(value));
            const now = new Date();

            return date < now;
          }
        ),
      end_at: string()
        .nullable()
        .transform((value) => (value === '' ? null : value))
        .typeError('Must be a valid timestamp')
        .test(
          'is-at-least-1-hour-after-start',
          'End date must be at least 1 hour after start time',
          function (endValue) {
            const { start_at } = this.parent;

            if (!Number(endValue) || !start_at) {
              return true
            };

            const end = Number(endValue);
            const start = Number(start_at);

            if (isNaN(end) || isNaN(start)) return true;

            return end >= start + DATA_RETRIEVAL_INTERVAL.ONE_HOUR;
          }
        ),
        cycleSettings: createCycleSettingsValidationSchema(cycleSettingsMeasurement),
      }),
    [cycleSettingsMeasurement],
  );

  const formik = useFormik<CycleFormValues>({
    initialValues: {
      name: cycle?.name ?? null,
      start_at: cycle?.start_at ? new Date(cycle?.start_at).getTime() : null,
      end_at: cycle?.end_at ? new Date(cycle?.end_at).getTime() : null,
      list: [],
      locationId: cycle?.locationId ?? clientId,
      contractId: cycle?.contractId ?? contractId,
      screenId: screenId ?? null,
      cycleSettings: { mode: null },
    },
    validationSchema: cycleValidationSchema,
    onSubmit: async (values) => {
      const { cycleSettings, ...rest } = values;
      const payload: Record<string, unknown> = { id, ...rest, oldScreenId: screenId };

      if (!screenId) {
        delete payload.oldScreenId;
      }

      if (!values.screenId) {
        delete payload.screenId;
      }

      if (cycle.status === CycleStatus.InActive && cycleSettings?.mode) {
        payload.cycleSettings = applyDefaultTolerances(cycleSettings);
      }

      try {
        setCycleConflict(null);
        await updateCycle(payload);
      } catch (error) {
        const conflict = getCycleConflictError(error);

        if (conflict) {
          setCycleConflict(conflict);
          return;
        }

        throw error;
      }

      const path = getPath();
      navigate(path);
    },
  });

  const isDataLoaded = useMemo(() => (contracts !== null && locations !== null && screens !== null && !!cycle), [contracts, locations, screens, cycle]);

  const selectedContract = useMemo(
    () => contracts?.find((item) => item.id === formik.values.contractId) || null,
    [contracts, formik.values.contractId],
  );
  const selectedContractMeasurement = selectedContract?.isCelsius === false ? CycleMeasurement.Fahrenheit : CycleMeasurement.Celsius;

  const disabledContract = formik.values.locationId === null || locations === null || locations.length === 0;
  const disabledScreen = disabledContract || formik.values.contractId === null;
  const disabledController = disabledScreen;
  const disableChannelsThermocouples = !editedController || formik.values.list.length === 0;
  const disabledSubmit = Object.keys(formik.errors).length > 0;

  const channelOptions = useMemo<{ name: string; value: string }[]>(() => {
    if (editedController && (editedController.serial_number in channelsByController)) {
      const channels = channelsByController[editedController.serial_number];
      return channels
        .map(channel => ({
          name: channel.name?.trim() || channel.channel,
          value: channel.channel,
        }));
    }

    return Object.keys(CHANNELS).map(key => ({
      name: key,
      value: key,
    }));
  }, [editedController, channelsByController]);

  const changeStatus = async (event: React.FormEvent<HTMLFormElement>, status: CycleStatus) => {
    event.preventDefault();

    try {
      setCycleConflict(null);
      await updateCycle({ id, status, start_at: status === CycleStatus.Active ? new Date().getTime() : undefined });
    } catch (error) {
      const conflict = getCycleConflictError(error);

      if (conflict) {
        setCycleConflict(conflict);
        return;
      }

      throw error;
    }

    const path = getPath();
    navigate(path);
  }

  const fetchContractsByLocationId = async (locationId: string) => {
    await getContractsByLocationId({ locationId });
  }

  const fetchScreensByContractId = async (contractId: string) => {
    await getScreensByContractId({ contractId });
  }

  const fetchControllersByOrganizationId = async (organizationId) => {
    try {
      await getControllersByOrganizationId(organizationId);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchCycleData = async (cycleId: string) => {
    const cycle = await getCycleById(cycleId);

    const formValue: CycleFormValues = {
      name: null,
      start_at: null,
      end_at: null,
      list: null,
      locationId: null,
      contractId: null,
      screenId,
      cycleSettings: { mode: null },
    }

    for (const key in cycle) {
      if (Object.hasOwn(formik.values, key)) {
        formValue[key] = key === 'start_at' || key === 'end_at' ? new Date(cycle[key]).getTime() : cycle[key];
      }

      if (key === KEY_TO_CONVERT_LIST) {
        const list = cycle[key].map((item) => (
          {
            id: item.controller.id,
            name: item.controller.name,
            serial_number: item.controller.serial_number,
            is_active: item.controller.is_active,
            thermocouples: item?.thermocouples || [],
            channels: item?.channels || [],
          }
        ))
        formValue['list'] = list;
      }
    }

    const channelsByController: Record<string, IChannelName[]> = {};

    Object.values(cycle.channelsIot).forEach((data) => {
      const channel = data?.name;
      if (!channelsByController[channel.serial]) {
        channelsByController[channel.serial] = [];
      }
      channelsByController[channel.serial].push(data?.name);
    });

    Object.keys(channelsByController).forEach((key) => {
      if (channelsByController[key].length) {
        setChannelsByController((prev) => ({
          ...prev,
          [key]: channelsByController[key],
        }));
      }
    });

    formik.setValues(formValue);

    if (cycle.status === CycleStatus.InActive) {
      const plannedRecipe = await getPlannedRecipe(cycleId);
      setPlannedRecipeRaw(plannedRecipe);
    }
  }

  const getLocationsOptions = useCallback((locations: Location[]) => {
    if (locations.length === 0) {
      return [];
    }

    const options: Option[] = generateOptions(locations);
    return options;
  }, [locations]);

  const getContractOptions = useCallback((locations: Location[], contracts: Contract[]) => {
    if (locations.length === 0 || contracts.length === 0) {
      return [];
    }

    const options: Option[] = generateOptions(contracts);
    return options;
  }, [locations, contracts]);

  const getScreenOptions = useCallback((contracts: Contract[], screens: Screen[]) => {
    if (contracts.length === 0 || screens.length === 0) {
      return [];
    }

    const options: Option[] = generateOptions(screens);
    return options;
  }, [contracts, screens]);

  const generateOptions = (listData: Location[] | Contract[] | Screen[]): Option[] => {
    return listData.map((data) => {
      const option: Option = { id: data.id, value: data.name };

      return option;
    });
  }

  const getSelectedOption = (listData: Location[] | Contract[] | Screen[], id: string | undefined): Option | null => {
    if (listData.length === 0 || !id) {
      return null;
    }

    const data = listData.find((option) => option.id === id);

    return { id, value: data.name };
  }

  const selectOption = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = event.target;

    const selectedValue: SelectedValue = value as unknown as SelectedValue;

    if (name === 'locationId') {
      formik.setFieldValue(name, selectedValue.id);
      formik.setFieldValue('contractId', null);
      formik.setFieldValue('screenId', null);
      formik.setTouched({ [name]: true });

      await fetchContractsByLocationId(selectedValue.id);
      return;
    }

    if (name === 'contractId') {
      formik.setFieldValue(name, selectedValue.id);
      formik.setFieldValue('screenId', null);
      formik.setTouched({ [name]: true });

      await fetchScreensByContractId(selectedValue.id);
      return;
    }

    if (name === 'cycleSettings.mode') {
      if (selectedValue.id === '') {
        formik.setFieldValue('cycleSettings', { mode: null });
      } else {
        formik.setFieldValue(name, selectedValue.id);
      }
      formik.setFieldTouched(name, true);
      return;
    }

    formik.setFieldValue(name, selectedValue.id);
    formik.setTouched({ [name]: true });
    return;
  }

  const cancelEdit = () => {
    const path = getPath();
    navigate(path);
  }

  const handleSelect = (cycleEntityName: ComponentNameCycleList, event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = event.target;

    let list = formik.values.list;

    if (!formik.touched?.list) {
      formik.setFieldTouched('list', true);
    }

    if (cycleEntityName === ComponentNameCycleList.Controllers) {
      const controller = controllers.find((controller) => controller.serial_number === value);

      if (checked) {
        list.push({
          id: controller.id,
          name: controller.name,
          serial_number: controller.serial_number,
          is_active: controller.is_active,
          thermocouples: [],
          channels: []
        })
      };

      if (!checked) {
        list = list.filter((item) => item.serial_number !== value);
      }

      if (!checked && editedController?.serial_number === value) {
        setEditedController(null);
        clearChannels();
      }

      if (checked && editedController?.serial_number === null) {
        const controller = controllers.find((controller) => controller.serial_number === value);
        setEditedController(controller);
      }
      formik.setFieldValue('list', list);

      return;
    }

    if (cycleEntityName === ComponentNameCycleList.Channels || cycleEntityName === ComponentNameCycleList.Thermocouples) {
      const keyField = cycleEntityName.toLocaleLowerCase();
      const updatedList = list.map((item) => {
        if (item.serial_number === editedController?.serial_number && checked) {
          item[keyField] = [...item[keyField], value];
          return item;
        };

        if (item.serial_number === editedController?.serial_number && !checked) {
          item[keyField] = item[keyField].filter((item) => item !== value);
          return item;
        };

        return item;
      });

      formik.setFieldValue('list', updatedList);

      return;
    }
  }

  const convertListToView = (list: ItemList[]) => {
    const selectionReport = list.map((item) => {
      const listChannels = getChannelNames(item.channels, channelsByController, item.serial_number);

      return `${item.name} | ${item.serial_number}: ${[...listChannels, ...item.thermocouples].join(',')}`
    });

    return selectionReport;
  };

  const getTitleByEntity = (cycleEntityName: ComponentNameCycleList) => {
    const name = capitalizeFirstLetter(cycleEntityName);

    if (cycleEntityName === ComponentNameCycleList.Controllers) {
      return name;
    } else {
      const controller = editedController ? controllers.find((item) => item.serial_number === editedController?.serial_number) : null;
      const titleController = controller ? `for ${controller.name}` : '';
      return `${name} ${titleController}`;
    }
  };

  const getEntityValue = (name: ComponentNameCycleList) => {
    if (formik.values.list.length === 0) {
      return null;
    }

    if (name === ComponentNameCycleList.Controllers) {
      return formik.values.list.map((item) => item.serial_number);
    }

    if (name === ComponentNameCycleList.Channels || name === ComponentNameCycleList.Thermocouples) {
      const keyField = name.toLocaleLowerCase();
      const values = formik.values.list.find((item) => item.serial_number === editedController?.serial_number);

      return (values) ? values[keyField] : null
    };

    return null;
  };

  const changeEditingController = (serial_number: string) => {
    if (serial_number === editedController?.serial_number) {
      setEditedController(null);
      clearChannels();
    } else {
      const controller = controllers.find((controller) => controller.serial_number === serial_number);
      setEditedController(controller);

      const list = formik.values.list;
      const isControllerUsed = list.some((item) => item.serial_number === serial_number);

      if (!isControllerUsed) {
        list.push({
          id: controller.id,
          name: controller.name,
          serial_number: controller.serial_number,
          is_active: controller.is_active,
          thermocouples: [],
          channels: []
        })

        formik.setFieldValue('list', list);
      };
    }
  };

  const availableControllersForModal = useMemo(() => {
    const usedSerialNumbers = new Set(formik.values.list.map((item) => item.serial_number));

    return controllers.filter((controller) => controller.is_active && !usedSerialNumbers.has(controller.serial_number));
  }, [controllers, formik.values.list]);

  const removeController = (serial_number: string) => {
    const updatedList = formik.values.list.filter((item) => item.serial_number !== serial_number);

    if (!formik.touched?.list) {
      formik.setFieldTouched('list', true);
    }

    formik.setFieldValue('list', updatedList);

    if (editedController?.serial_number === serial_number) {
      setEditedController(null);
      clearChannels();
    }
  };

  const addControllers = (serialNumbers: string[]) => {
    if (serialNumbers.length === 0) {
      return;
    }

    const list = formik.values.list;
    let lastAddedController: Controller | null = null;

    serialNumbers.forEach((serial_number) => {
      const controller = controllers.find((item) => item.serial_number === serial_number);
      const alreadyUsed = list.some((item) => item.serial_number === serial_number);

      if (!controller || alreadyUsed) {
        return;
      }

      list.push({
        id: controller.id,
        name: controller.name,
        serial_number: controller.serial_number,
        is_active: controller.is_active,
        thermocouples: [],
        channels: []
      });
      lastAddedController = controller;
    });

    if (!formik.touched?.list) {
      formik.setFieldTouched('list', true);
    }

    formik.setFieldValue('list', list);

    if (lastAddedController) {
      setEditedController(lastAddedController);
    }

    setIsAddControllerModalOpen(false);
  };

  const selectAllOptions = (cycleEntityName: ComponentNameCycleList, options: ConfigurationCycleOption[]) => {
    const list = formik.values.list;

    if (cycleEntityName === ComponentNameCycleList.Controllers) {
      const updatedList = options.map((option) => {
        const controllerByOption = controllers.find((controller) => controller.serial_number === option.value);
        const usedItem = list.find((oldItem) => oldItem.serial_number === controllerByOption.serial_number);

        if (usedItem) {
          return usedItem;
        }

        return {
          id: controllerByOption.id,
          name: controllerByOption.name,
          serial_number: controllerByOption.serial_number,
          thermocouples: [],
          channels: []
        }
      })

      formik.setFieldValue('list', updatedList);
      return;
    }

    if (cycleEntityName === ComponentNameCycleList.Channels || cycleEntityName === ComponentNameCycleList.Thermocouples) {
      const keyField = cycleEntityName.toLocaleLowerCase();
      const values = (cycleEntityName === ComponentNameCycleList.Channels) ? Object.keys(CHANNELS) : Object.keys(THERMOCOUPLES);

      const updatedList = list.map((item) => {
        item[keyField] = values;
        return item;
      });

      formik.setFieldValue('list', updatedList);
      return;
    }

    return;
  }

  const resetAllOptions = (cycleEntityName: ComponentNameCycleList) => {

    if (cycleEntityName === ComponentNameCycleList.Controllers) {
      setEditedController(null);
      clearChannels();
      formik.setFieldValue('list', []);
      return;
    }

    if (cycleEntityName === ComponentNameCycleList.Channels || cycleEntityName === ComponentNameCycleList.Thermocouples) {
      const list = formik.values.list;
      const keyField = cycleEntityName.toLocaleLowerCase();

      const updatedList = list.map((item) => {
        item[keyField] = [];
        return item;
      });

      formik.setFieldValue('list', updatedList);
      return;
    }
  }

  const changeDataTime = async (name: string, value: number) => {
    await formik.setFieldValue(name, value);
    await formik.setFieldTouched(name, true);
  };

  const submitForm = (e: React.FormEvent) => {
    e.preventDefault();
  };

  const submitFormByBtn = (e: React.MouseEvent) => {
    e.preventDefault();
    formik.submitForm();
  };

  const channelsLoader = async (controllerId: string, serial_number: string) => {
    const channels = await getChannels(controllerId);

    if (channels?.length) {
      setChannelsByController((prev) => ({ ...prev, [serial_number]: channels }));
    }
  };

  useEffect(() => {
    fetchCycleData(id);
  }, [id]);

  useEffect(() => {
    setCycleSettingsMeasurement(selectedContractMeasurement);
  }, [selectedContractMeasurement]);

  useEffect(() => {
    if (!plannedRecipeRaw || formik.touched.cycleSettings) {
      return;
    }

    let cycleSettings: CycleSettingsFormValues = { mode: null };

    if (plannedRecipeRaw.source === 'recipe' && plannedRecipeRaw.settings) {
      cycleSettings = convertCycleSettingsUnits(
        plannedRecipeRaw.settings,
        CycleMeasurement.Celsius,
        cycleSettingsMeasurement,
      );
    } else if (plannedRecipeRaw.source === 'pending' && plannedRecipeRaw.settings) {
      cycleSettings = plannedRecipeRaw.settings;
    }

    formik.setFieldValue('cycleSettings', cycleSettings, false);
  }, [plannedRecipeRaw, cycleSettingsMeasurement]);

  useEffect(() => {
    fetchControllersByOrganizationId(user.organization.id);

    return () => {
      clearChannels();
    };
  }, [user]);

  useEffect(() => {
    getContractById(contractId);
    fetchScreensByContractId(contractId);
  }, [contractId]);

  useEffect(() => {
    fetchContractsByLocationId(clientId);
  }, [clientId]);

  useEffect(() => {
    getLocations();

    return () => {
      clearChannels();
    };
  }, []);

  useEffect(() => {
    if (editedController) {
      setLoadingChannels(true);
      channelsLoader(editedController.id, editedController.serial_number).finally(() => setLoadingChannels(false));
    }
  }, [editedController]);

  useEffect(() => {
    if (editedController || formik.values.list.length === 0 || controllers.length === 0) {
      return;
    }

    const [firstItem] = formik.values.list;
    const controller = controllers.find((item) => item.serial_number === firstItem.serial_number);

    if (controller) {
      setEditedController(controller);
    }
  }, [formik.values.list, controllers, editedController]);

  return (
    <>
      {!isDataLoaded && (
        <div className="h-full flex items-center justify-center"><SpinnerLoader /></div>
      )}

      {isDataLoaded && (
        <>
          <div className='flex gap-10 mb-5'>
            {STATUS_LIST.map((item: ICycleStatusListItem, index: number) =>
              <StatusListItem
                key={`status-cycle-item-${index}`}
                item={item}
                cycleStatus={cycle.status}
                onClick={(value) => {
                  if (cycle.status !== item.value)
                    changeStatus(value, item.value)
                }}
              />
            )}
          </div>
          <form className="p-add-cycle font-medium pb-4 md:pb-25" onSubmit={submitForm}>
            <div className='rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark p-8'>
              <div className="p-edit-cycle__top-fields flex flex-col gap-y-1.5">
                <InputControl
                  type="text"
                  name='name'
                  label="Cycle name"
                  placeholder="Enter cycle name"
                  value={formik.values.name || ''}
                  customOnChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  showError={(formik.touched.name && formik.errors.name) ? true : false}
                  errorText={`${formik.errors.name}`}
                  customBlockClassName={ROW_BLOCK_CLASS}
                  customLabelClassName={ROW_LABEL_CLASS}
                />
                <div className="flex w-full gap-x-6">
                  <InputDateTimeControl
                    label='Start time'
                    name='start_at'
                    value={formik.values.start_at ? formik.values.start_at : ''}
                    handleChange={changeDataTime}
                    showError={formik.touched.start_at && formik.errors.start_at}
                    errorText={formik.errors.start_at}
                    onBlur={formik.handleBlur}
                    customBlockClassName={`${ROW_BLOCK_CLASS} flex-1`}
                    customLabelClassName={ROW_LABEL_CLASS}
                  />
                  <InputDateTimeControl
                    label='End time'
                    name='end_at'
                    value={formik.values.end_at ? formik.values.end_at : ''}
                    handleChange={changeDataTime}
                    showError={formik.touched.end_at && formik.errors.end_at}
                    errorText={formik.errors.end_at}
                    onBlur={formik.handleBlur}
                    customBlockClassName={`${ROW_BLOCK_CLASS} flex-1`}
                    customLabelClassName={ROW_LABEL_CLASS}
                  />
                </div>
                <SelectOption
                  name='locationId'
                  onChange={selectOption}
                  options={getLocationsOptions(locations)}
                  selectedOption={getSelectedOption(locations, formik.values.locationId)}
                  title='Client'
                  placeholder='Select your client'
                  showError={(formik.touched.locationId && formik.errors.locationId) ? true : false}
                  errorText={`${formik.errors.locationId}`}
                  customBlockClassName={ROW_BLOCK_CLASS}
                  customLabelClassName={ROW_LABEL_CLASS}
                />
                <SelectOption
                  disabled={disabledContract}
                  name='contractId'
                  onChange={selectOption}
                  options={getContractOptions(locations, contracts)}
                  selectedOption={getSelectedOption(contracts, formik.values.contractId)}
                  title='Contract'
                  placeholder='Select your contract'
                  showError={(formik.touched.contractId && formik.errors.contractId) ? true : false}
                  errorText={`${formik.errors.contractId}`}
                  customBlockClassName={ROW_BLOCK_CLASS}
                  customLabelClassName={ROW_LABEL_CLASS}
                />
                <SelectOption
                  disabled={disabledScreen}
                  name='screenId'
                  onChange={selectOption}
                  options={getScreenOptions(contracts, screens)}
                  selectedOption={getSelectedOption(screens, formik.values.screenId)}
                  title='Screen'
                  placeholder='Select your screen'
                  showError={(formik.touched.screenId && formik.errors.screenId) ? true : false}
                  errorText={`${formik.errors.screenId}`}
                  customBlockClassName={ROW_BLOCK_CLASS}
                  customLabelClassName={ROW_LABEL_CLASS}
                />
              </div>
              <div className="p-edit-cycle__divider h-1 py-6 after:block after:w-full after:border after:border-dashed after:border-stroke after:content-['']" />
              <div className="p-edit-cycle__form flex flex-col gap-y-4">
                <div className="p-edit-cycle__controller-tabs-row flex flex-wrap items-end justify-between gap-2">
                  <ControllerTabs
                    list={formik.values.list}
                    editingController={editedController?.serial_number ?? null}
                    onSelectTab={changeEditingController}
                    onRemoveTab={removeController}
                    disabled={disabledController}
                  />
                  <InteractiveButton
                    type={ButtonType.PrimaryInverse}
                    size={ElementSize.MD}
                    text="+ Controller"
                    onClick={() => setIsAddControllerModalOpen(true)}
                    disabled={disabledController}
                  />
                </div>
                <div className="p-edit-cycle__configurations w-full grid max-md:grid-cols-[100%] md:grid-cols-2 items-stretch justify-between gap-x-4 gap-y-8 py-6">
                  {loadingChannels ? (
                    <div className="flex h-full w-full items-center justify-center">
                      <SpinnerLoader />
                    </div>
                  ) : (
                    <ConfigurationCycle
                      disabled={disableChannelsThermocouples}
                      editingController={editedController?.serial_number}
                      layout="tags"
                      listOptions={channelOptions}
                      onChange={(event) => handleSelect(ComponentNameCycleList.Channels, event)}
                      selectedValues={getEntityValue(ComponentNameCycleList.Channels)}
                      title={getTitleByEntity(ComponentNameCycleList.Channels)}
                      selectAllOptions={(data) => selectAllOptions(ComponentNameCycleList.Channels, data)}
                      resetAllOptions={() => resetAllOptions(ComponentNameCycleList.Channels)}
                    />
                  )}
                  <ConfigurationCycle
                    disabled={disableChannelsThermocouples}
                    editingController={editedController?.serial_number}
                    layout="tags"
                    listOptions={thermocoupleOptions}
                    onChange={(event) => handleSelect(ComponentNameCycleList.Thermocouples, event)}
                    selectedValues={getEntityValue(ComponentNameCycleList.Thermocouples)}
                    title={getTitleByEntity(ComponentNameCycleList.Thermocouples)}
                    selectAllOptions={(data) => selectAllOptions(ComponentNameCycleList.Thermocouples, data)}
                    resetAllOptions={() => resetAllOptions(ComponentNameCycleList.Thermocouples)}
                  />
                </div>

                <div className="flex min-h-6 pb-2 items-center justify-start">
                  {
                    ((formik.errors?.list?.length > 0 && formik.touched?.list))
                      ? (<p className="text-sm text-red">{`${formik.errors?.list}`}</p>)
                      : (<p className="text-sm text-success">You must select at least one channel or thermocouple for the selected controller</p>)
                  }
                </div>
                <div className='p-edit-cycle__cycles overflow-auto flex items-center h-40 border border-stroke rounded text-black dark:text-white p-2 text-base'>
                  {!(formik.values.list.length !== 0) ? (<p className='w-full text-center text-2xl font-semibold'>No controllers selected</p>) : (
                    <ul className='flex flex-auto flex-col w-full h-full'>
                      {convertListToView(formik.values.list).map((item) => (
                        <li className='self-start pb-2' key={item}>
                          <p>{item}</p>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <AddControllerModal
                  isVisible={isAddControllerModalOpen}
                  availableControllers={availableControllersForModal}
                  onClose={() => setIsAddControllerModalOpen(false)}
                  onAdd={addControllers}
                />

                {cycle.status === CycleStatus.InActive && (
                  <div className="p-edit-cycle__cycle-settings">
                    <CycleSettingsInput
                      values={formik.values.cycleSettings}
                      onChange={(newSettings) => {
                        formik.setFieldValue('cycleSettings', newSettings);
                        setTimeout(() => formik.validateField('cycleSettings'), 0);
                      }}
                      onModeChange={selectOption}
                      onBlur={(field) => formik.setFieldTouched(field, true)}
                      errors={formik.errors.cycleSettings as any}
                      touched={formik.touched.cycleSettings as any}
                      contractTemperatureUnit={cycleSettingsMeasurement}
                      onRecipeSelect={async (recipeId: string) => {
                        const recipe = await getRecipe(recipeId);

                        if (recipe.mode === CycleSettingsEnum.PreHeat) {
                          const celsiusPreheatSettings = {
                            ...formik.values.cycleSettings,
                            mode: CycleSettingsEnum.PreHeat,
                            rate: recipe.rate,
                            finalTemp: recipe.finalTemp,
                            soakUpper: recipe.soakUpper,
                            soakLower: recipe.soakLower,
                            selectedRecipeId: recipe.id,
                          };
                          const preheatSettings = convertCycleSettingsUnits(
                            celsiusPreheatSettings,
                            CycleMeasurement.Celsius,
                            cycleSettingsMeasurement,
                          );

                          formik.setFieldValue('cycleSettings', preheatSettings);
                          formik.setFieldTouched('cycleSettings.rate', true);
                          formik.setFieldTouched('cycleSettings.finalTemp', true);
                          formik.setFieldTouched('cycleSettings.soakUpper', true);
                          formik.setFieldTouched('cycleSettings.soakLower', true);
                          setTimeout(() => formik.validateField('cycleSettings'), 0);
                        } else if (recipe.mode === CycleSettingsEnum.PostWeld) {
                          const steps = await getRecipeSteps(recipeId);

                          const celsiusPostWeldSettings = {
                            ...formik.values.cycleSettings,
                            mode: CycleSettingsEnum.PostWeld,
                            name: recipe.name,
                            rateUpperTolerance: recipe.rateUpperTolerance,
                            rateLowerTolerance: recipe.rateLowerTolerance,
                            holdUpperTolerance: recipe.holdUpperTolerance,
                            holdLowerTolerance: recipe.holdLowerTolerance,
                            selectedRecipeId: recipe.id,
                            steps: steps.map((step: any, index: number) => ({
                              mode: step.mode,
                              durationDays: step.durationDays || 0,
                              durationHours: step.durationHours || 0,
                              durationMinutes: step.durationMinutes || 0,
                              heatingRate: step.heatingRate,
                              heatingLimit: step.heatingLimit,
                              order: index,
                              isCelsius: true,
                              duration: step.duration,
                              units: CycleMeasurement.Celsius,
                            })),
                          };
                          const postWeldSettings = convertCycleSettingsUnits(
                            celsiusPostWeldSettings,
                            CycleMeasurement.Celsius,
                            cycleSettingsMeasurement,
                          );

                          formik.setFieldValue('cycleSettings', postWeldSettings);
                          formik.setFieldTouched('cycleSettings.name', true);
                          formik.setFieldTouched('cycleSettings.rateUpperTolerance', true);
                          formik.setFieldTouched('cycleSettings.rateLowerTolerance', true);
                          formik.setFieldTouched('cycleSettings.holdUpperTolerance', true);
                          formik.setFieldTouched('cycleSettings.holdLowerTolerance', true);
                          setTimeout(() => formik.validateField('cycleSettings'), 0);
                        }
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="h-28" aria-hidden="true" />
            <div className="c-edit-cycle__sticky-footer fixed bottom-0 left-8 right-8 z-50 w-[calc(100%-4rem)] rounded-t-sm border-t border-stroke bg-white px-6 py-4 shadow-[0_-4px_10px_rgba(0,0,0,0.06)] dark:border-strokedark dark:bg-boxdark lg:left-[329px] lg:w-[calc(100%-23rem)]">
              <CycleConflictBanner conflict={cycleConflict} onDismiss={() => setCycleConflict(null)} />
              <div className="c-edit-cycle__actions flex flex-row justify-end gap-x-4">
                <InteractiveButton
                  size={ElementSize.LG}
                  type={ButtonType.PrimaryInverse}
                  text="Cancel"
                  onClick={cancelEdit}
                />
                <InteractiveButton
                  size={ElementSize.LG}
                  type={ButtonType.Primary}
                  text="Save"
                  disabled={disabledSubmit}
                  submit
                  onClick={submitFormByBtn}
                />
              </div>
            </div>
          </form>
        </>
      )}
    </>
  );
};

const mapState = (state: RootState) => ({
  controllers: state.controllers.controllers,
  locations: state.locations.locations,
  contracts: state.contracts.contracts,
  screens: state.screens.screens,
  cycle: state.cycles.cycle,
  user: state.user.profile,
  channels: state.channels.channels,
});

const mapDispatch = (dispatch: Dispatch) => ({
  getControllersByOrganizationId: dispatch.controllers.getAllByOrganizationId,
  updateCycle: dispatch.cycles.updateCycle,
  getLocations: dispatch.locations.getLocations,
  getContractsByLocationId: dispatch.contracts.getContractsByLocation,
  getScreensByContractId: dispatch.screens.getAllByContractId,
  getContractById: dispatch.contracts.getContract,
  getCycleById: dispatch.cycles.getOneWithLatestIot,
  getChannels: dispatch.channels.getChannels,
  clearChannels: dispatch.channels.resetChannelState,
  getPlannedRecipe: dispatch.cycles.getPlannedRecipe,
  getRecipe: dispatch.recipes.getRecipe,
  getRecipeSteps: dispatch.recipes.getRecipeSteps,
});

export default connect(mapState, mapDispatch)(EditCycle);
